import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
  BookOpen, Plus, Edit2, Trash2, X, Loader2,
  User, Calendar, CheckCircle, Clock, Star, MessageSquare,
  Paperclip, Link2, Download, AlertTriangle, RotateCcw
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminActivities = () => {
  const [activities, setActivities] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [gradeModalOpen, setGradeModalOpen] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [filter, setFilter] = useState('all');

  // Create form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    student_id: '',
    due_date: '',
    attachment_url: ''
  });

  // Grade form state
  const [gradeData, setGradeData] = useState({
    grade: '',
    feedback: '',
    review_status: 'reviewed'
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [activitiesRes, studentsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/activities?page_size=200`),
        axios.get(`${API_URL}/api/admin/students?page_size=200`)
      ]);
      setActivities(activitiesRes.data.data ?? activitiesRes.data);
      setStudents((studentsRes.data.data ?? studentsRes.data).filter(s => s.status === 'approved'));
    } catch (err) {
      console.error('Failed to fetch data:', err);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const openModal = () => {
    setFormData({ title: '', description: '', student_id: '', due_date: '', attachment_url: '' });
    setModalOpen(true);
  };

  const closeModal = () => setModalOpen(false);

  const openGradeModal = (activity) => {
    setSelectedActivity(activity);
    setGradeData({
      grade: activity.grade ?? '',
      feedback: activity.feedback || '',
      review_status: activity.review_status === 'needs_revision' ? 'needs_revision' : 'reviewed'
    });
    setGradeModalOpen(true);
  };

  const closeGradeModal = () => {
    setGradeModalOpen(false);
    setSelectedActivity(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.student_id || !formData.description) {
      toast.error('Please fill in required fields');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        title: formData.title,
        description: formData.description,
        student_id: formData.student_id,
        due_date: formData.due_date ? new Date(formData.due_date).toISOString() : null,
        attachment_url: formData.attachment_url || null
      };

      await axios.post(`${API_URL}/api/admin/activities`, payload);
      toast.success('Activity created and student notified');
      closeModal();
      fetchData();
    } catch (err) {
      console.error('Failed to create activity:', err);
      toast.error('Failed to create activity');
    } finally {
      setSaving(false);
    }
  };

  const handleGrade = async (e) => {
    e.preventDefault();

    // Grade is required only when marking as reviewed
    if (gradeData.review_status === 'reviewed') {
      const parsedGrade = parseFloat(gradeData.grade);
      if (Number.isNaN(parsedGrade) || parsedGrade < 0 || parsedGrade > 100) {
        toast.error('Please enter a valid grade (0-100)');
        return;
      }
    }

    setSaving(true);
    try {
      const payload = {
        review_status: gradeData.review_status,
        feedback: gradeData.feedback || null,
        grade: gradeData.review_status === 'reviewed' ? parseFloat(gradeData.grade) : null
      };

      await axios.put(`${API_URL}/api/admin/activities/${selectedActivity.id}/grade`, payload);

      if (gradeData.review_status === 'needs_revision') {
        toast.success('Student notified to revise and resubmit');
      } else {
        toast.success('Grade saved and student notified');
      }

      closeGradeModal();
      fetchData();
    } catch (err) {
      console.error('Failed to review activity:', err);
      toast.error('Failed to save review');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (activityId) => {
    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/api/admin/activities/${activityId}`);
      toast.success('Activity deleted');
      setDeleteConfirmId(null);
      fetchData();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to delete activity';
      toast.error(msg);
    } finally {
      setDeleting(false);
    }
  };

  const getStudentName = (studentId) =>
    students.find(s => s.id === studentId)?.name || 'Unknown';

  const getStatusInfo = (activity) => {
    if (activity.review_status === 'needs_revision') {
      return { label: 'Needs Revision', color: 'bg-[#F5A623]/10 text-[#F5A623]', icon: RotateCcw };
    }
    if (activity.grade !== null && activity.grade !== undefined) {
      return { label: 'Graded', color: 'bg-[#4CAF50]/10 text-[#4CAF50]', icon: Star };
    }
    if (activity.submitted) {
      return { label: 'Submitted', color: 'bg-[#1A2E6E]/10 text-[#1A2E6E]', icon: CheckCircle };
    }
    return { label: 'Pending', color: 'bg-[#E8EAF0] text-[#1A2E6E]/60', icon: Clock };
  };

  const filteredActivities = activities.filter(a => {
    if (filter === 'all') return true;
    if (filter === 'pending') return !a.submitted;
    if (filter === 'submitted') return a.submitted && a.review_status === 'pending';
    if (filter === 'needs_revision') return a.review_status === 'needs_revision';
    if (filter === 'graded') return a.review_status === 'reviewed';
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-[#D72B2B] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-activities-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-[#1A2E6E] font-['Sora']">
            Activities
          </h1>
          <p className="text-[#1A2E6E]/60 mt-1">
            Create and grade student activities
          </p>
        </div>
        <button
          onClick={openModal}
          data-testid="create-activity-btn"
          className="flex items-center gap-2 px-5 py-3 bg-[#D72B2B] text-white rounded-xl hover:bg-[#B71C1C] transition-colors font-medium"
        >
          <Plus size={20} />
          New Activity
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-[#E8EAF0] p-4 text-center">
          <p className="text-2xl font-bold text-[#1A2E6E]">{activities.length}</p>
          <p className="text-sm text-[#1A2E6E]/60">Total</p>
        </div>
        <div className="bg-[#E8EAF0]/60 rounded-xl border border-[#E8EAF0] p-4 text-center">
          <p className="text-2xl font-bold text-[#1A2E6E]/70">
            {activities.filter(a => !a.submitted).length}
          </p>
          <p className="text-sm text-[#1A2E6E]/60">Pending</p>
        </div>
        <div className="bg-[#F5A623]/5 rounded-xl border border-[#F5A623]/20 p-4 text-center">
          <p className="text-2xl font-bold text-[#F5A623]">
            {activities.filter(a => a.submitted && a.review_status === 'pending').length}
          </p>
          <p className="text-sm text-[#1A2E6E]/60">To Review</p>
        </div>
        <div className="bg-[#4CAF50]/5 rounded-xl border border-[#4CAF50]/20 p-4 text-center">
          <p className="text-2xl font-bold text-[#4CAF50]">
            {activities.filter(a => a.review_status === 'reviewed').length}
          </p>
          <p className="text-sm text-[#1A2E6E]/60">Reviewed</p>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 bg-white rounded-xl p-1.5 border border-[#E8EAF0] w-fit flex-wrap">
        {[
          { id: 'all', label: 'All' },
          { id: 'pending', label: 'Pending' },
          { id: 'submitted', label: 'To Review' },
          { id: 'needs_revision', label: 'Needs Revision' },
          { id: 'graded', label: 'Reviewed' }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setFilter(tab.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              filter === tab.id
                ? 'bg-[#1A2E6E] text-white'
                : 'text-[#1A2E6E]/70 hover:text-[#1A2E6E] hover:bg-[#1A2E6E]/5'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Activities List */}
      <div className="bg-white rounded-xl border border-[#E8EAF0] overflow-hidden">
        {filteredActivities.length > 0 ? (
          <div className="divide-y divide-[#E8EAF0]">
            {filteredActivities.map((activity, index) => {
              const statusInfo = getStatusInfo(activity);
              const StatusIcon = statusInfo.icon;
              return (
                <motion.div
                  key={activity.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: index * 0.03 }}
                  className="p-4 lg:p-6"
                >
                  <div className="flex flex-col lg:flex-row lg:items-start gap-4">
                    {/* Icon */}
                    <div className={`w-14 h-14 rounded-xl flex items-center justify-center shrink-0 ${statusInfo.color.split(' ')[0]}`}>
                      <BookOpen className={`w-7 h-7 ${statusInfo.color.split(' ')[1]}`} />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <h3 className="font-semibold text-[#1A2E6E] text-lg">
                          {activity.title}
                        </h3>
                        <span className={`flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium shrink-0 ${statusInfo.color}`}>
                          <StatusIcon size={12} />
                          {statusInfo.label}
                        </span>
                      </div>

                      <p className="text-sm text-[#1A2E6E]/70 mb-2 line-clamp-2">
                        {activity.description}
                      </p>

                      <div className="flex flex-wrap items-center gap-4 text-sm text-[#1A2E6E]/60">
                        <span className="flex items-center gap-1.5">
                          <User size={14} />
                          {getStudentName(activity.student_id)}
                        </span>
                        {activity.due_date && (
                          <span className="flex items-center gap-1.5">
                            <Calendar size={14} />
                            Due: {new Date(activity.due_date).toLocaleDateString('en-GB')}
                          </span>
                        )}
                        {activity.grade !== null && activity.grade !== undefined && (
                          <span className="flex items-center gap-1.5 text-[#4CAF50] font-medium">
                            <Star size={14} />
                            Grade: {activity.grade}%
                          </span>
                        )}
                      </div>

                      {/* Teacher Attachment */}
                      {activity.attachment_url && (
                        <div className="mt-2">
                          <a
                            href={activity.attachment_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 text-xs text-[#1A2E6E] hover:text-[#D72B2B] transition-colors"
                          >
                            <Paperclip size={12} />
                            View Attachment
                          </a>
                        </div>
                      )}

                      {/* Submission preview */}
                      {activity.submitted && activity.submission_content && (
                        <div className="mt-3 p-3 bg-[#FAFAF7] rounded-lg">
                          <p className="text-xs font-medium text-[#1A2E6E]/60 mb-1">Student Submission:</p>
                          <p className="text-sm text-[#1A2E6E] line-clamp-3">
                            {activity.submission_content}
                          </p>
                          {activity.submission_files?.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-2">
                              {activity.submission_files.map((url, i) => (
                                <a
                                  key={i}
                                  href={url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 text-xs text-[#1A2E6E] hover:text-[#D72B2B] bg-[#E8EAF0] px-2 py-1 rounded"
                                >
                                  <Download size={10} />
                                  File {i + 1}
                                </a>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 shrink-0">
                      {activity.submitted && activity.review_status !== 'reviewed' && (
                        <button
                          onClick={() => openGradeModal(activity)}
                          className="flex items-center gap-2 px-4 py-2 bg-[#4CAF50] text-white rounded-lg hover:bg-[#388E3C] transition-colors text-sm font-medium"
                        >
                          <Star size={16} />
                          Review
                        </button>
                      )}
                      {activity.review_status === 'reviewed' && (
                        <button
                          onClick={() => openGradeModal(activity)}
                          className="flex items-center gap-2 px-4 py-2 bg-[#1A2E6E]/10 text-[#1A2E6E] rounded-lg hover:bg-[#1A2E6E]/20 transition-colors text-sm font-medium"
                        >
                          <Edit2 size={16} />
                          Edit
                        </button>
                      )}
                      {deleteConfirmId === activity.id ? (
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleDelete(activity.id)}
                            disabled={deleting}
                            className="px-3 py-2 bg-[#D72B2B] text-white rounded-lg text-xs font-medium hover:bg-[#B71C1C] disabled:opacity-50"
                          >
                            {deleting ? <Loader2 size={12} className="animate-spin" /> : 'Confirm'}
                          </button>
                          <button
                            onClick={() => setDeleteConfirmId(null)}
                            className="px-3 py-2 border border-[#E8EAF0] text-[#1A2E6E]/60 rounded-lg text-xs font-medium hover:bg-[#E8EAF0]"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setDeleteConfirmId(activity.id)}
                          className="p-2 text-[#1A2E6E]/40 hover:text-[#D72B2B] hover:bg-[#D72B2B]/10 rounded-lg transition-colors"
                          title="Delete activity"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        ) : (
          <div className="p-12 text-center">
            <BookOpen className="w-16 h-16 text-[#1A2E6E]/20 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-[#1A2E6E] mb-2">No Activities</h3>
            <p className="text-[#1A2E6E]/60 mb-4">
              {filter === 'all' ? 'Create your first activity' : 'No activities in this category'}
            </p>
          </div>
        )}
      </div>

      {/* Create Activity Modal */}
      <AnimatePresence>
        {modalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={closeModal}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
            >
              <div className="flex items-center justify-between p-6 border-b border-[#E8EAF0]">
                <h2 className="text-xl font-bold text-[#1A2E6E]">New Activity</h2>
                <button onClick={closeModal} className="p-2 text-[#1A2E6E]/60 hover:text-[#1A2E6E] rounded-lg">
                  <X size={20} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-5">
                <div>
                  <label className="block text-sm font-medium text-[#1A2E6E] mb-2">
                    Title <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="e.g., Write about your weekend"
                    className="w-full px-4 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] outline-none"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#1A2E6E] mb-2">
                    Description <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Activity instructions..."
                    rows={4}
                    className="w-full px-4 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] outline-none resize-none"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#1A2E6E] mb-2">
                    Student <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.student_id}
                    onChange={(e) => setFormData({ ...formData, student_id: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] outline-none bg-white"
                    required
                  >
                    <option value="">Select a student</option>
                    {students.map(student => (
                      <option key={student.id} value={student.id}>
                        {student.name} ({student.email})
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#1A2E6E] mb-2">
                    Due Date
                  </label>
                  <input
                    type="datetime-local"
                    value={formData.due_date}
                    onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#1A2E6E] mb-2">
                    <span className="flex items-center gap-1.5">
                      <Paperclip size={14} />
                      Attachment URL
                    </span>
                  </label>
                  <input
                    type="url"
                    value={formData.attachment_url}
                    onChange={(e) => setFormData({ ...formData, attachment_url: e.target.value })}
                    placeholder="https://... or paste a media library URL"
                    className="w-full px-4 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] outline-none"
                  />
                  <p className="text-xs text-[#1A2E6E]/50 mt-1.5">
                    Paste a link to a file or resource for the student to reference.
                  </p>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="flex-1 py-3 border border-[#E8EAF0] text-[#1A2E6E] rounded-xl hover:bg-[#1A2E6E]/5 font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="flex-1 py-3 bg-[#D72B2B] text-white rounded-xl hover:bg-[#B71C1C] font-medium disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {saving ? <Loader2 size={18} className="animate-spin" /> : <Plus size={18} />}
                    Create
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Review/Grade Modal */}
      <AnimatePresence>
        {gradeModalOpen && selectedActivity && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={closeGradeModal}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
            >
              <div className="flex items-center justify-between p-6 border-b border-[#E8EAF0]">
                <h2 className="text-xl font-bold text-[#1A2E6E]">Review Activity</h2>
                <button onClick={closeGradeModal} className="p-2 text-[#1A2E6E]/60 hover:text-[#1A2E6E] rounded-lg">
                  <X size={20} />
                </button>
              </div>

              <div className="p-6">
                {/* Activity Info */}
                <div className="bg-[#FAFAF7] rounded-xl p-4 mb-6">
                  <h3 className="font-semibold text-[#1A2E6E] mb-1">{selectedActivity.title}</h3>
                  <p className="text-sm text-[#1A2E6E]/60 mb-1">{getStudentName(selectedActivity.student_id)}</p>
                  <p className="text-sm text-[#1A2E6E]/70 mb-3">{selectedActivity.description}</p>

                  {/* Teacher attachment */}
                  {selectedActivity.attachment_url && (
                    <div className="mb-3">
                      <a
                        href={selectedActivity.attachment_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-xs text-[#1A2E6E] hover:text-[#D72B2B] bg-[#E8EAF0] px-2.5 py-1.5 rounded-lg transition-colors"
                      >
                        <Paperclip size={12} />
                        Your Attachment
                      </a>
                    </div>
                  )}

                  {selectedActivity.submission_content && (
                    <div className="border-t border-[#E8EAF0] pt-3 mt-3">
                      <p className="text-xs font-medium text-[#1A2E6E]/60 mb-1">Student Submission:</p>
                      <p className="text-sm text-[#1A2E6E]">{selectedActivity.submission_content}</p>
                      {selectedActivity.submission_files?.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-2">
                          {selectedActivity.submission_files.map((url, i) => (
                            <a
                              key={i}
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-xs text-[#1A2E6E] hover:text-[#D72B2B] bg-[#E8EAF0] px-2 py-1 rounded"
                            >
                              <Download size={10} />
                              File {i + 1}
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <form onSubmit={handleGrade} className="space-y-5">
                  {/* Review Status */}
                  <div>
                    <label className="block text-sm font-medium text-[#1A2E6E] mb-2">
                      Review Decision <span className="text-red-500">*</span>
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => setGradeData(prev => ({ ...prev, review_status: 'reviewed' }))}
                        className={`flex items-center justify-center gap-2 p-3 rounded-xl border-2 text-sm font-medium transition-all ${
                          gradeData.review_status === 'reviewed'
                            ? 'border-[#4CAF50] bg-[#4CAF50]/5 text-[#4CAF50]'
                            : 'border-[#E8EAF0] text-[#1A2E6E]/60 hover:border-[#4CAF50]/40'
                        }`}
                      >
                        <CheckCircle size={16} />
                        Reviewed
                      </button>
                      <button
                        type="button"
                        onClick={() => setGradeData(prev => ({ ...prev, review_status: 'needs_revision' }))}
                        className={`flex items-center justify-center gap-2 p-3 rounded-xl border-2 text-sm font-medium transition-all ${
                          gradeData.review_status === 'needs_revision'
                            ? 'border-[#F5A623] bg-[#F5A623]/5 text-[#F5A623]'
                            : 'border-[#E8EAF0] text-[#1A2E6E]/60 hover:border-[#F5A623]/40'
                        }`}
                      >
                        <RotateCcw size={16} />
                        Needs Revision
                      </button>
                    </div>
                  </div>

                  {/* Grade — only shown when marking as reviewed */}
                  {gradeData.review_status === 'reviewed' && (
                    <div>
                      <label className="block text-sm font-medium text-[#1A2E6E] mb-2">
                        Grade (0-100) <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        value={gradeData.grade}
                        onChange={(e) => setGradeData({ ...gradeData, grade: e.target.value })}
                        placeholder="85"
                        className="w-full px-4 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] outline-none text-2xl font-bold text-center"
                        required
                      />
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-[#1A2E6E] mb-2">
                      Feedback {gradeData.review_status === 'needs_revision' && <span className="text-[#F5A623] text-xs">(explain what to improve)</span>}
                    </label>
                    <textarea
                      value={gradeData.feedback}
                      onChange={(e) => setGradeData({ ...gradeData, feedback: e.target.value })}
                      placeholder={
                        gradeData.review_status === 'needs_revision'
                          ? 'Please revise the conclusion and add more examples...'
                          : 'Great job! Keep practicing...'
                      }
                      rows={4}
                      className="w-full px-4 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] outline-none resize-none"
                    />
                  </div>

                  <div className="flex gap-3 pt-4">
                    <button
                      type="button"
                      onClick={closeGradeModal}
                      className="flex-1 py-3 border border-[#E8EAF0] text-[#1A2E6E] rounded-xl hover:bg-[#1A2E6E]/5 font-medium"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={saving}
                      className={`flex-1 py-3 text-white rounded-xl font-medium disabled:opacity-50 flex items-center justify-center gap-2 ${
                        gradeData.review_status === 'needs_revision'
                          ? 'bg-[#F5A623] hover:bg-[#E09000]'
                          : 'bg-[#4CAF50] hover:bg-[#388E3C]'
                      }`}
                    >
                      {saving ? (
                        <Loader2 size={18} className="animate-spin" />
                      ) : gradeData.review_status === 'needs_revision' ? (
                        <RotateCcw size={18} />
                      ) : (
                        <Star size={18} />
                      )}
                      {gradeData.review_status === 'needs_revision' ? 'Send for Revision' : 'Save Grade'}
                    </button>
                  </div>
                </form>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminActivities;
